
import React, { useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import MainLayout from '@/components/layout/MainLayout';
import SubscriptionDetailComponent from '@/components/subscriptions/SubscriptionDetail';
import { useSubscriptionDetail } from '@/hooks/useSubscription';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '@/hooks/useLanguage';

const SubscriptionDetail = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { translate } = useLanguage();
  const { subscription, invoices, isLoading, error } = useSubscriptionDetail(id || '');

  const handleRefreshData = useCallback(() => {
    if (id) {
      queryClient.invalidateQueries({ queryKey: ['subscription', id] });
      queryClient.invalidateQueries({ queryKey: ['subscription-services', id] });
      queryClient.invalidateQueries({ queryKey: ['subscription-invoices', id] });
    }
  }, [id, queryClient]);

  // Loading state UI
  if (isLoading) {
    return (
      <MainLayout>
        <div className="mb-8 flex justify-between items-center">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-60 w-full" />
        </div>
      </MainLayout>
    );
  }

  // Error state UI
  if (error || !subscription) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] p-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center text-red-600 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-2">{translate('subscriptions.notFound')}</h2>
          <p className="text-muted-foreground mb-6">{error || translate('subscriptions.doesNotExist')}</p>
          <Link to="/subscriptions">
            <Button variant="outline">
              <ChevronLeft className="h-4 w-4 mr-2" />
              {translate('common.back')} {translate('common.to')} {translate('navigation.subscriptions')}
            </Button>
          </Link>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="mb-6">
        <Link to="/subscriptions" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft className="h-4 w-4 mr-1" />
          {translate('common.back')} {translate('common.to')} {translate('navigation.subscriptions')}
        </Link>
      </div>

      <SubscriptionDetailComponent 
        subscription={subscription}
        invoices={invoices}
        onRefresh={handleRefreshData}
      />
    </MainLayout>
  );
};

export default SubscriptionDetail;
